/**
 * Navigation latérale du CRM.
 *
 * Remplace la barre horizontale, qui saturait : neuf entrées y étaient
 * compressées, réduites à des émojis dès qu'on rétrécissait la fenêtre. Une
 * colonne accepte les intitulés en toutes lettres, permet de grouper par
 * métier, et libère toute la largeur pour le contenu.
 *
 * Aucun écran n'a disparu : les mêmes entrées, dans le même ordre logique,
 * simplement rangées par thème. Les entrées d'administration restent réservées
 * aux admins — le serveur les refuse de toute façon, mais afficher un lien
 * qu'on ne peut pas suivre n'aide personne.
 */
import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MARQUE } from '../marque';
import { C, E, RAYON, T } from '../theme';
import {
  IconeDashboard, IconeEntreprises, IconeKanban, IconeAgenda, IconeProposition,
  IconeDossier, IconeDossiers, IconeAdmin, IconeClientsWeb, IconeProfil,
  IconeDeconnexion, IconeMenu, IconeFermer, IconeReplier,
} from './Icones';

export const LARGEUR = 236;
export const LARGEUR_REPLIEE = 68;

const groupes = (role) => [
  {
    titre: 'Pilotage',
    liens: [{ to: '/', label: 'Tableau de bord', Icone: IconeDashboard }],
  },
  {
    titre: 'Commercial',
    liens: [
      { to: '/prospects', label: 'Entreprises', Icone: IconeEntreprises },
      { to: '/kanban', label: 'Kanban', Icone: IconeKanban },
      { to: '/agenda', label: 'Agenda', Icone: IconeAgenda },
      { to: '/proposition', label: 'Proposition', Icone: IconeProposition },
    ],
  },
  {
    titre: 'Financement',
    liens: [
      { to: '/dossier', label: 'Nouveau dossier', Icone: IconeDossier },
      { to: '/dossiers', label: 'Suivi des dossiers', Icone: IconeDossiers, badge: true },
    ],
  },
  ...(role === 'admin' ? [{
    titre: 'Administration',
    liens: [
      { to: '/admin', label: 'Administration', Icone: IconeAdmin },
      { to: '/clients-web', label: 'Accès clients', Icone: IconeClientsWeb },
    ],
  }] : []),
];

function Entree({ lien, actif, replie, alertCount, onClick }) {
  const [survol, setSurvol] = useState(false);
  const { Icone } = lien;
  return (
    <Link
      to={lien.to}
      onClick={onClick}
      onMouseEnter={() => setSurvol(true)}
      onMouseLeave={() => setSurvol(false)}
      title={replie ? lien.label : undefined}
      style={{
        display: 'flex', alignItems: 'center', gap: E.md,
        padding: replie ? '10px 0' : '9px 12px',
        justifyContent: replie ? 'center' : 'flex-start',
        margin: '1px 10px', borderRadius: RAYON.sm,
        textDecoration: 'none', fontSize: '13.5px',
        fontWeight: actif ? 600 : 500,
        color: actif ? '#fff' : 'rgba(255,255,255,0.66)',
        background: actif ? 'rgba(255,255,255,0.13)'
          : survol ? 'rgba(255,255,255,0.06)' : 'transparent',
        transition: 'background 120ms ease, color 120ms ease',
        position: 'relative',
      }}
    >
      <span style={{ display: 'flex', flexShrink: 0 }}><Icone /></span>
      {!replie && <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden',
                                 textOverflow: 'ellipsis' }}>{lien.label}</span>}
      {lien.badge && alertCount > 0 && (
        <span style={{
          background: C.danger, color: '#fff', borderRadius: RAYON.rond,
          fontSize: '10px', fontWeight: 700, lineHeight: '16px',
          padding: replie ? '0 4px' : '0 6px', minWidth: replie ? 'auto' : '18px',
          textAlign: 'center', flexShrink: 0,
          position: replie ? 'absolute' : 'static', top: replie ? 4 : undefined,
          right: replie ? 10 : undefined,
        }}>{alertCount}</span>
      )}
    </Link>
  );
}

export default function Sidebar({ user, onLogout, alertCount, isMobile, ouvert, setOuvert,
                                  replie, setReplie }) {
  const location = useLocation();

  // Sur mobile, changer de page referme le tiroir : sans cela il masque
  // l'écran qu'on vient d'ouvrir.
  useEffect(() => { if (isMobile) setOuvert(false); }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const estReplie = !isMobile && replie;
  const largeur = isMobile ? LARGEUR : (replie ? LARGEUR_REPLIEE : LARGEUR);

  const contenu = (
    <>
      {/* En-tête : logo et nom du cabinet */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: E.md,
        padding: estReplie ? `${E.lg} 0` : `${E.lg} ${E.lg}`,
        justifyContent: estReplie ? 'center' : 'flex-start',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        minHeight: '64px', boxSizing: 'border-box',
      }}>
        <img src={MARQUE.logo} alt="" style={{
          height: 34, width: 34, borderRadius: '50%', objectFit: 'cover', flexShrink: 0,
          background: '#fff',
        }} />
        {!estReplie && (
          <div style={{ overflow: 'hidden' }}>
            <div style={{ color: '#fff', fontSize: '14px', fontWeight: 700, lineHeight: 1.2 }}>
              {MARQUE.nom}
            </div>
            {MARQUE.activite && (
              <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '11px' }}>
                {MARQUE.activite}
              </div>
            )}
          </div>
        )}
        {isMobile && (
          <button onClick={() => setOuvert(false)} aria-label="Fermer le menu"
            style={{ marginLeft: 'auto', background: 'none', border: 'none',
                     color: 'rgba(255,255,255,0.7)', cursor: 'pointer', padding: 4,
                     display: 'flex' }}>
            <IconeFermer />
          </button>
        )}
      </div>

      {/* Entrées, groupées par métier */}
      <nav style={{ flex: 1, overflowY: 'auto', padding: `${E.md} 0` }}>
        {groupes(user.role).map(groupe => (
          <div key={groupe.titre} style={{ marginBottom: E.lg }}>
            {!estReplie && (
              <div style={{ ...T.sectionMin, color: 'rgba(255,255,255,0.34)',
                            padding: `0 ${E.xl} ${E.sm}` }}>
                {groupe.titre}
              </div>
            )}
            {estReplie && <div style={{ height: 1, background: 'rgba(255,255,255,0.07)',
                                        margin: `0 ${E.lg} ${E.sm}` }} />}
            {groupe.liens.map(lien => (
              <Entree
                key={lien.to}
                lien={lien}
                actif={location.pathname === lien.to}
                replie={estReplie}
                alertCount={alertCount}
                onClick={isMobile ? () => setOuvert(false) : undefined}
              />
            ))}
          </div>
        ))}
      </nav>

      {/* Pied : compte et déconnexion */}
      <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', padding: `${E.sm} 0 ${E.md}` }}>
        <Entree
          lien={{ to: '/profil', label: user.nom, Icone: IconeProfil }}
          actif={location.pathname === '/profil'}
          replie={estReplie}
          onClick={isMobile ? () => setOuvert(false) : undefined}
        />
        <button
          onClick={onLogout}
          title={estReplie ? 'Déconnexion' : undefined}
          style={{
            display: 'flex', alignItems: 'center', gap: E.md,
            width: `calc(100% - 20px)`, margin: '1px 10px',
            padding: estReplie ? '10px 0' : '9px 12px',
            justifyContent: estReplie ? 'center' : 'flex-start',
            background: 'transparent', border: 'none', borderRadius: RAYON.sm,
            color: 'rgba(255,255,255,0.55)', fontSize: '13.5px', fontWeight: 500,
            fontFamily: T.police, cursor: 'pointer', textAlign: 'left',
          }}
        >
          <span style={{ display: 'flex', flexShrink: 0 }}><IconeDeconnexion /></span>
          {!estReplie && <span>Déconnexion</span>}
        </button>

        {/* Replier : gagner de la largeur sur les écrans chargés (tableaux) */}
        {!isMobile && (
          <button
            onClick={() => setReplie(r => !r)}
            aria-label={replie ? 'Déplier le menu' : 'Replier le menu'}
            style={{
              display: 'flex', alignItems: 'center', gap: E.md,
              width: `calc(100% - 20px)`, margin: '1px 10px',
              padding: estReplie ? '10px 0' : '9px 12px',
              justifyContent: estReplie ? 'center' : 'flex-start',
              background: 'transparent', border: 'none', borderRadius: RAYON.sm,
              color: 'rgba(255,255,255,0.4)', fontSize: '12.5px',
              fontFamily: T.police, cursor: 'pointer',
            }}
          >
            <span style={{ display: 'flex', flexShrink: 0,
                           transform: replie ? 'rotate(180deg)' : 'none' }}>
              <IconeReplier />
            </span>
            {!estReplie && <span>Replier</span>}
          </button>
        )}
      </div>
    </>
  );

  // ── Mobile : barre supérieure + tiroir ────────────────────────────────────
  if (isMobile) {
    return (
      <>
        <header style={{
          position: 'fixed', top: 0, left: 0, right: 0, height: 56, zIndex: 90,
          background: C.marine, display: 'flex', alignItems: 'center',
          gap: E.md, padding: `0 ${E.lg}`,
        }}>
          <button onClick={() => setOuvert(true)} aria-label="Ouvrir le menu"
            style={{ background: 'none', border: 'none', color: '#fff',
                     cursor: 'pointer', padding: 4, display: 'flex' }}>
            <IconeMenu taille={22} />
          </button>
          <img src={MARQUE.logo} alt="" style={{ height: 30, width: 30, borderRadius: '50%',
                                          objectFit: 'cover', background: '#fff' }} />
          <span style={{ color: '#fff', fontSize: '15px', fontWeight: 700 }}>{MARQUE.nom}</span>
          {alertCount > 0 && (
            <span style={{ marginLeft: 'auto', background: C.danger, color: '#fff',
                           borderRadius: RAYON.rond, fontSize: '11px', fontWeight: 700,
                           padding: '2px 8px' }}>{alertCount}</span>
          )}
        </header>

        {ouvert && (
          <div onClick={() => setOuvert(false)} style={{
            position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.5)', zIndex: 95,
          }} />
        )}
        <aside style={{
          position: 'fixed', top: 0, bottom: 0, left: 0, width: LARGEUR, zIndex: 96,
          background: C.marine, display: 'flex', flexDirection: 'column',
          transform: ouvert ? 'translateX(0)' : `translateX(-${LARGEUR + 8}px)`,
          transition: 'transform 180ms ease',
          boxShadow: ouvert ? '0 0 32px rgba(0,0,0,0.35)' : 'none',
        }}>
          {contenu}
        </aside>
      </>
    );
  }

  // ── Bureau : colonne fixe ─────────────────────────────────────────────────
  return (
    <aside style={{
      position: 'fixed', top: 0, bottom: 0, left: 0, width: largeur, zIndex: 90,
      background: C.marine, display: 'flex', flexDirection: 'column',
      transition: 'width 160ms ease',
    }}>
      {contenu}
    </aside>
  );
}
